import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhoneIcon,
  LinkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudIcon,
  LockClosedIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  EnvelopeOpenIcon,
  ShieldExclamationIcon,
  ChartPieIcon,
  ServerIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ServerStackIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  CogIcon,
  InboxIcon,
  SparklesIcon,
  EyeIcon,
  ClockIcon,
  FaceSmileIcon,
  LightBulbIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Tab } from '@headlessui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  RadialLinearScale,
} from 'chart.js';
import { Doughnut, Bar, Radar, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  RadialLinearScale
);

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface AnalysisData {
  id?: string;
  domain_name: string;
  analysis_date?: string;
  page_title?: string;
  meta_description?: string;
  overall_bi_score?: number;
  security_score?: number;
  security_grade?: string;
  business_stage?: string;
  investment_readiness?: string;
  primary_industry?: string;
  secondary_industry?: string;
  business_model?: string;
  target_market?: string;
  product_type?: string;
  company_size?: string;
  year_established?: number;
  company_age?: number;
  business_maturity_score?: number;
  maturity_factors?: string[];
  digital_investment_level?: string;
  risk_level?: string;
  risk_factors?: string[];
  dns_records?: {
    a_records?: string[];
    aaaa_records?: string[];
    mx_records?: Array<{ priority: number; server: string }>;
    txt_records?: string[];
    ns_records?: string[];
    soa_record?: any;
    cname_records?: string[];
  };
  dns_provider?: string;
  hosting_provider?: string;
  cdn_provider?: string;
  email_providers?: string[];
  infrastructure_details?: any;
  http_headers?: {
    server?: string;
    powered_by?: string;
    cms?: string;
    response_code?: number;
    redirect_to?: string;
    protocol?: string;
  };
  security_headers?: {
    hsts?: string | boolean;
    csp?: boolean;
    x_frame_options?: string;
    x_content_type_options?: string;
    x_xss_protection?: string;
    referrer_policy?: string;
    permissions_policy?: boolean;
  };
  ssl_info?: {
    issuer?: string;
    subject?: string;
    valid_from?: string;
    valid_until?: string;
    days_remaining?: number;
    san_names?: string[];
    protocols?: string[];
  };
  meta_keywords?: string;
  technologies?: {
    cms?: string[];
    frameworks?: string[];
    analytics?: string[];
    ecommerce?: string[];
    features?: string[];
  };
  contact_info?: {
    emails?: string[];
    phones?: string[];
    addresses?: string[];
    social_media?: {
      facebook?: string;
      twitter?: string;
      linkedin?: string;
      instagram?: string;
    };
  };
  whois_info?: {
    registrar?: string;
    created_date?: string;
    expiry_date?: string;
    name_servers?: string[];
    privacy_protected?: boolean;
  };
  market_maturity_score?: number;
  growth_potential_score?: number;
  technology_leadership_score?: number;
  customer_success_score?: number;
  endpoints_found?: {
    robots_txt?: boolean;
    sitemap_xml?: boolean;
    security_txt?: boolean;
    humans_txt?: boolean;
    favicon_ico?: boolean;
    manifest_json?: boolean;
    browserconfig_xml?: boolean;
    ads_txt?: boolean;
  };
  seo_metrics?: {
    meta_tags_count?: number;
    og_tags_count?: number;
    schema_org?: boolean;
    multilingual?: boolean;
    content_marketing?: boolean;
  };
  compliance_info?: {
    privacy_policy?: boolean;
    terms_of_service?: boolean;
    cookie_policy?: boolean;
    gdpr_compliant?: boolean;
    compliance_score?: number;
  };
  mobile_info?: {
    responsive_design?: boolean;
    ios_app_url?: string;
    android_app_url?: string;
    amp_support?: boolean;
  };
  traffic_indicators?: {
    estimated_traffic?: string;
    indicators?: string[];
  };
  competitive_advantages?: string[];
  key_features?: string[];
  certifications?: string[];
  partnerships?: string[];
  pricing_info?: {
    prices_found?: string[];
    pricing_model?: string;
    free_tier?: boolean;
  };
  analysis_count?: number;
  last_analyzed?: string;
  status?: string;
  notes?: string;
  raw_analysis_output?: string;
}

interface DmarcDomain {
  id: string;
  domain_name: string;
  dmarc_policy: string;
  subdomain_policy: string;
  percentage: number;
  mail_sources: number;
  total_messages: number;
  forward_count: number;
  unknown_count: number;
  total_wf: number;
  compliant_messages: number;
  compliant_pct: number;
  spf_fail_count: number;
  dkim_fail_count: number;
  non_compliant: number;
  status: string;
  is_monitored: boolean;
  last_report_received: string;
  created_at: string;
  updated_at: string;
}

interface EmailIntelligenceStats {
  totalDomains: number;
  totalMessages: number;
  avgComplianceRate: number;
  totalSpfFails: number;
  totalDkimFails: number;
  domainsWithRejectPolicy: number;
  domainsWithQuarantinePolicy: number;
  domainsWithNonePolicy: number;
}

const BusinessIntelligence: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [error, setError] = useState('');
  const [recentAnalyses, setRecentAnalyses] = useState<AnalysisData[]>([]);
  
  // Operational Intelligence states
  const [operationalDomain, setOperationalDomain] = useState('');
  const [operationalData, setOperationalData] = useState<any>(null);
  const [loadingOperational, setLoadingOperational] = useState(false);
  const [operationalError, setOperationalError] = useState('');
  
  // Email Intelligence states
  const [dmarcDomains, setDmarcDomains] = useState<DmarcDomain[]>([]);
  const [emailStats, setEmailStats] = useState<EmailIntelligenceStats | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [sortBy, setSortBy] = useState<keyof DmarcDomain>('total_messages');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDomains, setTotalDomains] = useState(0);
  const domainsPerPage = 50;

  useEffect(() => {
    if (selectedTab === 0) {
      // Operational Intelligence - no automatic fetch
    } else if (selectedTab === 1) {
      fetchRecentAnalyses();
    } else {
      fetchDmarcData();
    }
  }, [selectedTab, currentPage, sortBy, sortOrder]);

  const fetchOperationalData = async () => {
    if (!operationalDomain) {
      setOperationalError('Please enter a domain name');
      return;
    }

    setLoadingOperational(true);
    setOperationalError('');
    setOperationalData(null);

    try {
      const response = await fetch(`/api/domains/by-name/${operationalDomain}`, {
        headers: {
          'X-API-Key': 'pager123'
        }
      });
      const data = await response.json();

      if (data.success) {
        setOperationalData(data.data);
      } else {
        setOperationalError(data.message || 'Domain not found');
      }
    } catch (error) {
      console.error('Error fetching domain data:', error);
      setOperationalError('Failed to fetch domain information');
    } finally {
      setLoadingOperational(false);
    }
  };

  const loadSampleDomain = (domain: string) => {
    setOperationalDomain(domain);
    setLoadingOperational(true);
    setOperationalError('');
    setOperationalData(null);

    // Hard-coded sample data for epubs.ai
    if (domain === 'epubs.ai') {
      setTimeout(() => {
        setOperationalData({
          id: 'sample-001',
          domain_name: 'epubs.ai',
          username: 'admin@epubs.ai',
          email: 'contact@epubs.ai',
          user_id: 'USR-EPUB-001',
          manager_email: 'mo@pager.ai',
          company_name: 'ePubs Digital Publishing',
          company_domain: 'epubs.ai',
          original_url: 'https://epubs.ai',
          lead_name: 'Samuel Mendez',
          account_type: 'Google',
          platform: 'Instantly',
          registrar: 'Namecheap',
          price: 12.99,
          cloudflare_zone_id: 'cf-zone-123456789',
          cloudflare_ruleset_id: 'cf-rule-987654321',
          nameservers: '{"ns1": "ns1.cloudflare.com", "ns2": "ns2.cloudflare.com"}',
          accounts_to_cancel: '[]',
          accounts_to_create: '["info@epubs.ai", "support@epubs.ai", "sales@epubs.ai"]',
          emails: '["info@epubs.ai", "support@epubs.ai", "sales@epubs.ai", "admin@epubs.ai"]',
          names: '["Info Team", "Support Team", "Sales Team", "Admin"]',
          completed_upload: true,
          created_domain: 'success',
          created_zone: 'active',
          dkim_added: true,
          verified_connection: true,
          finished: 'complete',
          canceled_date: null,
          canceled_domain: false,
          new_email_ts: '2024-01-15T10:30:00Z',
          zone_creation_ts: '2024-01-10T14:20:00Z',
          updated_dns: '2024-01-12T09:15:00Z',
          site_verification_record_added: '2024-01-11T16:45:00Z',
          oauth_result1: 'Connected',
          oauth_result2: 'Connected',
          oauth_result3: 'Connected',
          oauth_result4: null,
          oauth_result5: null,
          verify_result_1: 'info@epubs.ai: Connected',
          verify_result_2: 'support@epubs.ai: Connected',
          verify_result_3: 'sales@epubs.ai: Connected',
          verify_result_4: null,
          verify_result_5: null,
          bubble_unique_id: 'bubble-epub-001',
          bubble_creation_date: '2024-01-10T08:00:00Z',
          bubble_modified_date: '2024-01-20T15:30:00Z',
          bubble_creator: 'system',
          bubble_client_id: 'client-epub-001',
          bubble_workspace_id: 'ws-12345',
          created_at: '2024-01-10T08:00:00Z',
          updated_at: '2024-01-20T15:30:00Z',
          is_active: true,
          notes: 'This is an ebook publishing platform specializing in digital content distribution.',
          dns_provider: 'Cloudflare',
          hosting_provider: 'Vercel',
          total_dns_records: 12,
          last_dns_update: '2024-01-12T09:15:00Z',
          forwarding_domain: 'mail.epubs.ai',
          has_password: true,
          assigned_va: 'sarah.johnson',
          claimed_at: '2024-01-11T10:00:00Z',
          dns_records: [
            {
              record_type: 'A',
              name: '@',
              value: '76.76.21.21',
              ttl: 300,
              priority: null,
              created_at: '2024-01-10T14:00:00Z'
            },
            {
              record_type: 'AAAA',
              name: '@',
              value: '2606:4700:3030::6815:1515',
              ttl: 300,
              priority: null,
              created_at: '2024-01-10T14:00:00Z'
            },
            {
              record_type: 'MX',
              name: '@',
              value: 'mail.epubs.ai',
              ttl: 300,
              priority: 10,
              created_at: '2024-01-10T14:00:00Z'
            },
            {
              record_type: 'TXT',
              name: '@',
              value: 'v=spf1 include:_spf.google.com ~all',
              ttl: 300,
              priority: null,
              created_at: '2024-01-10T14:00:00Z'
            },
            {
              record_type: 'TXT',
              name: '_dmarc',
              value: 'v=DMARC1; p=reject; rua=mailto:dmarc@epubs.ai',
              ttl: 300,
              priority: null,
              created_at: '2024-01-11T16:00:00Z'
            }
          ]
        });
        setLoadingOperational(false);
      }, 500); // Simulate network delay
      return;
    }

    // For other domains, fetch from API
    fetch(`/api/domains/by-name/${domain}`, {
      headers: {
        'X-API-Key': 'pager123'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        setOperationalData(data.data);
      } else {
        setOperationalError(data.message || 'Domain not found');
      }
    })
    .catch(error => {
      console.error('Error fetching domain data:', error);
      setOperationalError('Failed to fetch domain information');
    })
    .finally(() => {
      setLoadingOperational(false);
    });
  };

  const fetchRecentAnalyses = async () => {
    try {
      const response = await fetch('/api/business-intelligence?sort=analysis_date&order=desc&limit=10');
      const data = await response.json();
      if (data.success) {
        setRecentAnalyses(data.data);
      }
    } catch (error) {
      console.error('Error fetching recent analyses:', error);
    }
  };

  const fetchDmarcData = async () => {
    setLoadingEmail(true);
    setEmailError('');
    try {
      // First, get the statistics from the dedicated endpoint
      const statsResponse = await fetch('/api/dmarc/domains/stats');
      const statsData = await statsResponse.json();
      
      if (statsData.success) {
        setEmailStats(statsData.stats);
        setTotalDomains(statsData.stats.totalDomains);
      }
      
      // Then fetch domains for display (with proper pagination)
      const offset = (currentPage - 1) * domainsPerPage;
      const response = await fetch(`/api/dmarc/domains?limit=${domainsPerPage * 2}&sortBy=${sortBy}&sortOrder=${sortOrder}`);
      const data = await response.json();
      
      if (data.success) {
        const allDomains = data.domains || [];
        
        // Sort and paginate domains for display
        const sortedDomains = sortDomains(allDomains, sortBy, sortOrder);
        const paginatedDomains = sortedDomains.slice(0, domainsPerPage);
        setDmarcDomains(paginatedDomains);
      } else {
        setEmailError('Failed to fetch DMARC data');
      }
    } catch (error) {
      console.error('Error fetching DMARC data:', error);
      setEmailError('Failed to connect to DMARC database');
    } finally {
      setLoadingEmail(false);
    }
  };

  const calculateEmailStats = (domains: DmarcDomain[]): EmailIntelligenceStats => {
    const totalDomains = domains.length;
    const totalMessages = domains.reduce((sum, d) => sum + (Number(d.total_messages) || 0), 0);
    const totalCompliant = domains.reduce((sum, d) => sum + (Number(d.compliant_messages) || 0), 0);
    const avgComplianceRate = totalMessages > 0 ? (totalCompliant / totalMessages) * 100 : 0;
    const totalSpfFails = domains.reduce((sum, d) => sum + (Number(d.spf_fail_count) || 0), 0);
    const totalDkimFails = domains.reduce((sum, d) => sum + (Number(d.dkim_fail_count) || 0), 0);
    const domainsWithRejectPolicy = domains.filter(d => d.dmarc_policy === 'reject').length;
    const domainsWithQuarantinePolicy = domains.filter(d => d.dmarc_policy === 'quarantine').length;
    const domainsWithNonePolicy = domains.filter(d => d.dmarc_policy === 'none').length;
    
    return {
      totalDomains,
      totalMessages,
      avgComplianceRate,
      totalSpfFails,
      totalDkimFails,
      domainsWithRejectPolicy,
      domainsWithQuarantinePolicy,
      domainsWithNonePolicy
    };
  };

  const sortDomains = (domains: DmarcDomain[], key: keyof DmarcDomain, order: 'asc' | 'desc') => {
    return [...domains].sort((a, b) => {
      let aVal: any = a[key];
      let bVal: any = b[key];
      
      // Convert to numbers for numeric fields
      const numericFields = ['percentage', 'mail_sources', 'total_messages', 'forward_count', 
        'unknown_count', 'total_wf', 'compliant_messages', 'compliant_pct', 
        'spf_fail_count', 'dkim_fail_count', 'non_compliant'];
      
      if (numericFields.includes(key)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      if (order === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const handleSort = (key: keyof DmarcDomain) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPageNumbers = () => {
    const totalPages = Math.ceil(totalDomains / domainsPerPage);
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
            i === currentPage
              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const analyzeDomain = async () => {
    if (!domain) {
      setError('Please enter a domain name');
      return;
    }

    setLoading(true);
    setAnalyzing(true);
    setError('');
    setAnalysisData(null);

    try {
      const response = await fetch('/api/business-intelligence/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });

      const data = await response.json();

      if (data.cached) {
        setAnalysisData(data.data);
        setAnalyzing(false);
        setLoading(false);
      } else {
        pollForResults(domain);
      }
    } catch (error) {
      setError('Failed to start analysis');
      setLoading(false);
      setAnalyzing(false);
    }
  };

  const pollForResults = async (domainName: string) => {
    const maxAttempts = 30;
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(`/api/business-intelligence/status/${domainName}`);
        const data = await response.json();

        if (data.status === 'complete') {
          const analysisResponse = await fetch(`/api/business-intelligence/${domainName}`);
          const analysisData = await analysisResponse.json();
          if (analysisData.success) {
            setAnalysisData(analysisData.data);
            setAnalyzing(false);
            setLoading(false);
            fetchRecentAnalyses();
          }
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 2000);
        } else {
          setError('Analysis is taking longer than expected. Please try again later.');
          setAnalyzing(false);
          setLoading(false);
        }
      } catch (error) {
        setError('Failed to check analysis status');
        setAnalyzing(false);
        setLoading(false);
      }
    };

    checkStatus();
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-green-600 bg-green-50 border-green-200';
      case 'B': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'D': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  // Chart generators for Email Intelligence
  const generatePolicyDistributionChart = () => {
    if (!emailStats) return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 0
      }]
    };
    
    return {
      labels: ['Reject', 'Quarantine', 'None'],
      datasets: [{
        data: [
          emailStats.domainsWithRejectPolicy,
          emailStats.domainsWithQuarantinePolicy,
          emailStats.domainsWithNonePolicy
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(107, 114, 128, 1)'
        ],
        borderWidth: 2
      }]
    };
  };

  const generateComplianceOverviewChart = () => {
    if (!emailStats || emailStats.totalMessages === 0) return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 0
      }]
    };
    
    const nonCompliantRate = 100 - emailStats.avgComplianceRate;
    
    return {
      labels: ['Compliant', 'Non-Compliant'],
      datasets: [{
        data: [emailStats.avgComplianceRate, nonCompliantRate],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgba(16, 185, 129, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 2
      }]
    };
  };

  const generateFailureTypesChart = () => {
    if (!emailStats) return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 0
      }]
    };
    
    return {
      labels: ['SPF Failures', 'DKIM Failures'],
      datasets: [{
        label: 'Failure Count',
        data: [emailStats.totalSpfFails, emailStats.totalDkimFails],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(245, 158, 11, 0.8)'
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(245, 158, 11, 1)'
        ],
        borderWidth: 2,
        borderRadius: 8
      }]
    };
  };

  const generateTopDomainsChart = () => {
    const topDomains = sortDomains(dmarcDomains, 'total_messages', 'desc').slice(0, 10);
    
    return {
      labels: topDomains.map(d => d.domain_name),
      datasets: [
        {
          label: 'Total Messages',
          data: topDomains.map(d => Number(d.total_messages) || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1
        },
        {
          label: 'Compliant Messages',
          data: topDomains.map(d => Number(d.compliant_messages) || 0),
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const generateBIScoresRadarData = (data: AnalysisData) => ({
    labels: [
      'Overall BI Score',
      'Market Maturity',
      'Growth Potential',
      'Tech Leadership',
      'Customer Success',
      'Business Maturity'
    ],
    datasets: [
      {
        label: 'Business Intelligence Scores',
        data: [
          data.overall_bi_score || 0,
          data.market_maturity_score || 0,
          data.growth_potential_score || 0,
          data.technology_leadership_score || 0,
          data.customer_success_score || 0,
          data.business_maturity_score || 0
        ],
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        borderBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
      }
    ]
  });

  const generateMaturitySecurityData = (data: AnalysisData) => ({
    labels: ['Business Maturity', 'Security Score'],
    datasets: [{
      label: 'Score',
      data: [
        data.business_maturity_score || 0,
        data.security_score || 0
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)', // Blue for Business Maturity
        'rgba(34, 197, 94, 0.8)'   // Green for Security
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(34, 197, 94, 1)'
      ],
      borderWidth: 2,
      borderRadius: 8,
    }]
  });

  const generateComplianceData = (data: AnalysisData) => {
    const compliantCount = data.compliance_info ? [
      data.compliance_info.privacy_policy,
      data.compliance_info.terms_of_service,
      data.compliance_info.cookie_policy,
      data.compliance_info.gdpr_compliant
    ].filter(Boolean).length : 0;
    
    const nonCompliantCount = 4 - compliantCount;

    return {
      labels: ['Compliant', 'Non-Compliant'],
      datasets: [
        {
          data: [compliantCount, nonCompliantCount],
          backgroundColor: ['#10B981', '#EF4444'],
          borderColor: ['#059669', '#DC2626'],
          borderWidth: 2
        }
      ]
    };
  };

  const generateSecurityHeadersData = (data: AnalysisData) => {
    if (!data.security_headers) return null;
    
    const configured = Object.values(data.security_headers).filter(Boolean).length;
    const notConfigured = Object.keys(data.security_headers).length - configured;

    return {
      labels: ['Configured', 'Not Configured'],
      datasets: [
        {
          data: [configured, notConfigured],
          backgroundColor: ['#3B82F6', '#F59E0B'],
          borderColor: ['#2563EB', '#D97706'],
          borderWidth: 2
        }
      ]
    };
  };

  const generateTechnologyStackData = (data: AnalysisData) => {
    if (!data.technologies) return null;

    const techCounts = {
      CMS: data.technologies.cms?.length || 0,
      Frameworks: data.technologies.frameworks?.length || 0,
      Analytics: data.technologies.analytics?.length || 0,
      'E-commerce': data.technologies.ecommerce?.length || 0,
      Features: data.technologies.features?.length || 0
    };

    return {
      labels: Object.keys(techCounts),
      datasets: [
        {
          label: 'Technologies Count',
          data: Object.values(techCounts),
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(139, 92, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(139, 92, 246, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(239, 68, 68, 1)'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const generateDNSRecordsData = (data: AnalysisData) => {
    if (!data.dns_records) return null;

    const recordCounts = {
      'A Records': data.dns_records.a_records?.length || 0,
      'AAAA Records': data.dns_records.aaaa_records?.length || 0,
      'MX Records': data.dns_records.mx_records?.length || 0,
      'TXT Records': data.dns_records.txt_records?.length || 0,
      'NS Records': data.dns_records.ns_records?.length || 0
    };

    return {
      labels: Object.keys(recordCounts),
      datasets: [
        {
          label: 'DNS Records Count',
          data: Object.values(recordCounts),
          backgroundColor: 'rgba(99, 102, 241, 0.8)',
          borderColor: 'rgba(99, 102, 241, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const maturitySecurityOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return context.parsed.y + '/100';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value: any) {
            return value + '%';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Intelligence Dashboard</h1>
          <p className="mt-2 text-gray-600">Comprehensive domain and email analysis platform</p>
        </div>

        {/* Tab Navigation */}
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-8">
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-700 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              <div className="flex items-center justify-center">
                <ServerStackIcon className="h-5 w-5 mr-2" />
                Operational Intelligence
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-700 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              <div className="flex items-center justify-center">
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Business Intelligence
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-700 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              <div className="flex items-center justify-center">
                <EnvelopeOpenIcon className="h-5 w-5 mr-2" />
                Email Intelligence
              </div>
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Operational Intelligence Panel */}
            <Tab.Panel>
              {/* Search Section */}
              <div className="bg-white shadow rounded-lg p-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter domain name (e.g., example.com)"
                        value={operationalDomain}
                        onChange={(e) => setOperationalDomain(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchOperationalData()}
                        disabled={loadingOperational}
                      />
                    </div>
                  </div>
                  <button
                    onClick={fetchOperationalData}
                    disabled={loadingOperational}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingOperational ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : (
                      <>
                        <ServerStackIcon className="-ml-1 mr-2 h-4 w-4" />
                        Search
                      </>
                    )}
                  </button>
                </div>

                {/* Sample Domain Quick Access */}
                {!operationalData && !loadingOperational && (
                  <div className="mt-3 flex items-center">
                    <span className="text-sm text-gray-600 mr-2">Try a sample:</span>
                    <button
                      onClick={() => loadSampleDomain('epubs.ai')}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                    >
                      <ServerStackIcon className="h-3 w-3 mr-1" />
                      epubs.ai
                    </button>
                  </div>
                )}

                {loadingOperational && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-md">
                    <div className="flex">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          Searching for {operationalDomain}...
                        </p>
                        <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {operationalError && (
                  <div className="mt-4 p-4 bg-red-50 rounded-md">
                    <div className="flex">
                      <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">{operationalError}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Domain Information Display */}
              {operationalData && (
                <div className="space-y-6">
                  {/* Domain Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-blue-700 text-white shadow rounded-lg p-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-3xl font-bold mb-2">{operationalData.domain_name}</h2>
                        <div className="flex items-center space-x-4 text-purple-100">
                          <span className="flex items-center">
                            <UserIcon className="h-5 w-5 mr-1" />
                            {operationalData.company_name || 'Unknown Company'}
                          </span>
                          <span className="flex items-center">
                            <CalendarIcon className="h-5 w-5 mr-1" />
                            Created: {operationalData.created_at ? new Date(operationalData.created_at).toLocaleDateString() : 'Unknown'}
                          </span>
                          {operationalData.assigned_va && (
                            <span className="flex items-center">
                              <UserIcon className="h-5 w-5 mr-1" />
                              Assigned: {operationalData.assigned_va}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {operationalData.forwarding_domain && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white">
                              <LinkIcon className="h-3 w-3 mr-1" />
                              {operationalData.forwarding_domain}
                            </span>
                          )}
                          {operationalData.bubble_workspace_id && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white">
                              Workspace: {operationalData.bubble_workspace_id}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                          operationalData.is_active ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
                        }`}>
                          {operationalData.is_active ? 'Active' : 'Inactive'}
                        </div>
                        {operationalData.canceled_domain && (
                          <div className="mt-2 inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-red-500/20 text-red-100">
                            Cancelled
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Main Information Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Domain Details */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <GlobeAltIcon className="h-5 w-5 mr-2 text-blue-600" />
                        Domain Information
                      </h3>
                      <dl className="space-y-3">
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Domain Name</dt>
                          <dd className="text-sm text-gray-900 font-mono">{operationalData.domain_name}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                          <dd className="text-sm text-gray-900">{operationalData.company_name || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Company Domain</dt>
                          <dd className="text-sm text-gray-900 font-mono">{operationalData.company_domain || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Original URL</dt>
                          <dd className="text-sm text-gray-900 truncate max-w-xs" title={operationalData.original_url}>
                            {operationalData.original_url || 'Not specified'}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Lead Name</dt>
                          <dd className="text-sm text-gray-900">{operationalData.lead_name || 'Not specified'}</dd>
                        </div>
                      </dl>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <EnvelopeIcon className="h-5 w-5 mr-2 text-green-600" />
                        Contact Information
                      </h3>
                      <dl className="space-y-3">
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Email</dt>
                          <dd className="text-sm text-gray-900">{operationalData.email || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Username</dt>
                          <dd className="text-sm text-gray-900">{operationalData.username || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">User ID</dt>
                          <dd className="text-sm text-gray-900 font-mono">{operationalData.user_id || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Manager Email</dt>
                          <dd className="text-sm text-gray-900">{operationalData.manager_email || 'Not specified'}</dd>
                        </div>
                      </dl>
                    </div>

                    {/* Technical Details */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <CogIcon className="h-5 w-5 mr-2 text-purple-600" />
                        Technical Details
                      </h3>
                      <dl className="space-y-3">
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                          <dd className="text-sm text-gray-900">{operationalData.account_type || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Platform</dt>
                          <dd className="text-sm text-gray-900">{operationalData.platform || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Registrar</dt>
                          <dd className="text-sm text-gray-900">{operationalData.registrar || 'Not specified'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">DNS Provider</dt>
                          <dd className="text-sm text-gray-900">{operationalData.dns_provider || 'Not analyzed'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Hosting Provider</dt>
                          <dd className="text-sm text-gray-900">{operationalData.hosting_provider || 'Not analyzed'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Cloudflare Zone ID</dt>
                          <dd className="text-sm text-gray-900 font-mono text-xs truncate max-w-xs" title={operationalData.cloudflare_zone_id}>
                            {operationalData.cloudflare_zone_id || 'Not configured'}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Total DNS Records</dt>
                          <dd className="text-sm text-gray-900">{operationalData.total_dns_records || 0}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Last DNS Update</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.last_dns_update ? new Date(operationalData.last_dns_update).toLocaleDateString() : 'Never'}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Status & Tracking */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ChartBarIcon className="h-5 w-5 mr-2 text-orange-600" />
                        Status & Tracking
                      </h3>
                      <dl className="space-y-3">
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Status</dt>
                          <dd className="text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              operationalData.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {operationalData.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Has Password</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.has_password ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Verified Connection</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.verified_connection ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">DKIM Added</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.dkim_added ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Site Verification</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.site_verification_record_added ? 
                              new Date(operationalData.site_verification_record_added).toLocaleDateString() : 
                              'Not verified'
                            }
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Created At</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.created_at ? new Date(operationalData.created_at).toLocaleString() : 'Unknown'}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                          <dd className="text-sm text-gray-900">
                            {operationalData.updated_at ? new Date(operationalData.updated_at).toLocaleString() : 'Unknown'}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Email Accounts Information */}
                  {(operationalData.accounts_to_create || operationalData.emails) && (
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <EnvelopeIcon className="h-5 w-5 mr-2 text-blue-600" />
                        Email Accounts
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {operationalData.accounts_to_create && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Accounts to Create</h4>
                            <div className="bg-gray-50 rounded-md p-3">
                              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-all">
                                {typeof operationalData.accounts_to_create === 'string' ? 
                                  operationalData.accounts_to_create : 
                                  JSON.stringify(operationalData.accounts_to_create, null, 2)
                                }
                              </pre>
                            </div>
                          </div>
                        )}
                        {operationalData.emails && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Email List</h4>
                            <div className="bg-gray-50 rounded-md p-3">
                              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-all">
                                {typeof operationalData.emails === 'string' ? 
                                  operationalData.emails : 
                                  JSON.stringify(operationalData.emails, null, 2)
                                }
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Verification Results */}
                  {(operationalData.verify_result_1 || operationalData.verify_result_2 || 
                    operationalData.verify_result_3 || operationalData.verify_result_4 || 
                    operationalData.verify_result_5) && (
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ShieldCheckIcon className="h-5 w-5 mr-2 text-indigo-600" />
                        Verification Results
                      </h3>
                      <div className="space-y-2">
                        {operationalData.verify_result_1 && (
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 mr-2">Result 1:</span>
                            <span className="text-sm text-gray-600">{operationalData.verify_result_1}</span>
                          </div>
                        )}
                        {operationalData.verify_result_2 && (
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 mr-2">Result 2:</span>
                            <span className="text-sm text-gray-600">{operationalData.verify_result_2}</span>
                          </div>
                        )}
                        {operationalData.verify_result_3 && (
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 mr-2">Result 3:</span>
                            <span className="text-sm text-gray-600">{operationalData.verify_result_3}</span>
                          </div>
                        )}
                        {operationalData.verify_result_4 && (
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 mr-2">Result 4:</span>
                            <span className="text-sm text-gray-600">{operationalData.verify_result_4}</span>
                          </div>
                        )}
                        {operationalData.verify_result_5 && (
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 mr-2">Result 5:</span>
                            <span className="text-sm text-gray-600">{operationalData.verify_result_5}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Information */}
                  {operationalData.notes && (
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                        Notes
                      </h3>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{operationalData.notes}</p>
                    </div>
                  )}

                  {/* DNS Records if available */}
                  {operationalData.dns_records && (
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ServerIcon className="h-5 w-5 mr-2 text-teal-600" />
                        DNS Records
                      </h3>
                      <div className="bg-gray-50 rounded-md p-4 overflow-x-auto">
                        <pre className="text-xs text-gray-800 font-mono">
                          {JSON.stringify(operationalData.dns_records, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Empty State */}
              {!operationalData && !loadingOperational && !operationalError && (
                <div className="bg-white shadow rounded-lg p-12 text-center">
                  <ServerStackIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No domain selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Search for a domain to view its operational information.
                  </p>
                  <div className="mt-6">
                    <p className="text-sm text-gray-600 mb-2">Try searching for a sample domain:</p>
                    <button
                      onClick={() => loadSampleDomain('epubs.ai')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ServerStackIcon className="h-4 w-4 mr-2 text-gray-400" />
                      View epubs.ai
                    </button>
                  </div>
                </div>
              )}
            </Tab.Panel>

            {/* Business Intelligence Panel */}
            <Tab.Panel>
              {/* Search Section */}
              <div className="bg-white shadow rounded-lg p-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter domain name (e.g., example.com)"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && analyzeDomain()}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <button
                    onClick={analyzeDomain}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <ChartBarIcon className="-ml-1 mr-2 h-4 w-4" />
                        Analyze
                      </>
                    )}
                  </button>
                </div>

                {analyzing && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-md">
                    <div className="flex">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          Analyzing {domain}... This may take 30-60 seconds.
                        </p>
                        <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-4 bg-red-50 rounded-md">
                    <div className="flex">
                      <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Analysis Results */}
              {analysisData && (
                <div>
                  {/* Executive Summary Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white shadow rounded-lg p-8 mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold mb-2">{analysisData.domain_name}</h2>
                        {analysisData.page_title && (
                          <p className="text-xl text-blue-100 mb-2">{analysisData.page_title}</p>
                        )}
                        {analysisData.meta_description && (
                          <p className="text-blue-100 mb-4">{analysisData.meta_description}</p>
                        )}
                        
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {analysisData.primary_industry && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                              <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                              {analysisData.primary_industry}
                            </span>
                          )}
                          {analysisData.business_model && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                              <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                              {analysisData.business_model}
                            </span>
                          )}
                          {analysisData.target_market && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                              {analysisData.target_market}
                            </span>
                          )}
                          {analysisData.investment_readiness && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/80 text-white">
                              {analysisData.investment_readiness}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Key Metrics Dashboard */}
                      <div className="grid grid-cols-2 gap-6 mt-6 md:mt-0 md:ml-8">
                        <div className="bg-white/20 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-white">{analysisData.overall_bi_score || 0}</div>
                          <div className="text-sm text-blue-100">BI Score</div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-white">{analysisData.security_grade || 'N/A'}</div>
                          <div className="text-sm text-blue-100">Security Grade</div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4 text-center">
                          <div className="text-lg font-semibold text-white">{analysisData.business_stage || 'Unknown'}</div>
                          <div className="text-sm text-blue-100">Business Stage</div>
                        </div>
                        <div className="bg-white/20 rounded-lg p-4 text-center">
                          <div className="text-lg font-semibold text-white">{analysisData.company_size || 'Unknown'}</div>
                          <div className="text-sm text-blue-100">Company Size</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comprehensive Dashboard Layout */}
                  <div className="space-y-8">
                    
                    {/* Business Intelligence Scorecard */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <ChartBarIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Business Intelligence Scorecard
                      </h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Scores Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="text-center">
                            <div className={`text-4xl font-bold mb-2 ${getScoreColor(analysisData.overall_bi_score || 0).replace('border-', 'text-').replace('bg-', '').replace('-50', '-600')}`}>
                              {analysisData.overall_bi_score || 0}
                            </div>
                            <div className="text-sm text-gray-600">Overall BI Score</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold mb-2 text-purple-600">{analysisData.market_maturity_score || 0}</div>
                            <div className="text-sm text-gray-600">Market Maturity</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold mb-2 text-green-600">{analysisData.growth_potential_score || 0}</div>
                            <div className="text-sm text-gray-600">Growth Potential</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-bold mb-2 text-blue-600">{analysisData.technology_leadership_score || 0}</div>
                            <div className="text-sm text-gray-600">Tech Leadership</div>
                          </div>
                          <div className="text-center col-span-2">
                            <div className="text-3xl font-bold mb-2 text-orange-600">{analysisData.customer_success_score || 0}</div>
                            <div className="text-sm text-gray-600">Customer Success</div>
                          </div>
                        </div>
                        
                        {/* Radar Chart */}
                        <div className="flex items-center justify-center">
                          <div className="w-full h-80">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">BI Scores Overview</h4>
                            <Radar data={generateBIScoresRadarData(analysisData)} options={radarOptions} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Business Profile & Risk Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Business Profile */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                          <BuildingOfficeIcon className="h-6 w-6 mr-2 text-green-600" />
                          Business Profile
                        </h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-500">Primary Industry</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.primary_industry || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Secondary Industry</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.secondary_industry || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Business Model</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.business_model || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Target Market</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.target_market || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Product Type</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.product_type || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Company Age</span>
                              <div className="text-lg font-semibold text-gray-900">
                                {analysisData.year_established ? `${analysisData.company_age || new Date().getFullYear() - analysisData.year_established} years` : 'Unknown'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Investment Readiness */}
                          <div className="border-t pt-4">
                            <span className="text-sm font-medium text-gray-500">Investment Readiness</span>
                            <div className="text-lg font-semibold text-green-700">{analysisData.investment_readiness || 'Unknown'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Risk Analysis */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                          <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-yellow-600" />
                          Risk Assessment
                        </h3>
                        <div className="space-y-4">
                          {/* Business Maturity and Security Score Chart */}
                          <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-4">Performance Metrics</h4>
                            <div className="h-64">
                              <Bar data={generateMaturitySecurityData(analysisData)} options={maturitySecurityOptions} />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-500">Digital Investment</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.digital_investment_level || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Risk Level</span>
                              <div className={`text-lg font-semibold ${
                                analysisData.risk_level === 'High' ? 'text-red-600' :
                                analysisData.risk_level === 'Medium' ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>{analysisData.risk_level || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Business Maturity</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.business_maturity_score || 0}/100</div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Security Score</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.security_score || 0}/100</div>
                            </div>
                          </div>
                          
                          {analysisData.risk_factors && analysisData.risk_factors.length > 0 && (
                            <div className="border-t pt-4">
                              <span className="text-sm font-medium text-gray-500">Risk Factors</span>
                              <ul className="mt-2 space-y-1">
                                {analysisData.risk_factors.slice(0, 3).map((factor, index) => (
                                  <li key={index} className="text-sm text-gray-700">• {factor}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Technology & Infrastructure Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Technology Stack */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <BoltIcon className="h-5 w-5 mr-2 text-purple-600" />
                          Technology Stack
                        </h3>
                        <div className="space-y-4">
                          {/* Technology Chart */}
                          {generateTechnologyStackData(analysisData) && (
                            <div className="h-64">
                              <Bar data={generateTechnologyStackData(analysisData)!} options={barOptions} />
                            </div>
                          )}
                          
                          {/* Technology Details */}
                          {analysisData.technologies && (
                            <div className="mt-4 space-y-3">
                              {analysisData.technologies.cms && analysisData.technologies.cms.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">CMS ({analysisData.technologies.cms.length})</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {analysisData.technologies.cms.slice(0, 3).map((tech: string, index: number) => (
                                      <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                        {tech}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {analysisData.technologies.frameworks && analysisData.technologies.frameworks.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Frameworks ({analysisData.technologies.frameworks.length})</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {analysisData.technologies.frameworks.slice(0, 3).map((tech: string, index: number) => (
                                      <span key={index} className="inline-block px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                        {tech}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Infrastructure */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <CloudIcon className="h-5 w-5 mr-2 text-blue-600" />
                          Infrastructure
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm font-medium text-gray-500">DNS Provider</span>
                            <div className="text-sm text-gray-900">{analysisData.dns_provider || 'Unknown'}</div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">Hosting Provider</span>
                            <div className="text-sm text-gray-900">{analysisData.hosting_provider || 'Unknown'}</div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">CDN Provider</span>
                            <div className="text-sm text-gray-900">{analysisData.cdn_provider || 'None detected'}</div>
                          </div>
                          {analysisData.email_providers && analysisData.email_providers.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Email Providers</span>
                              <div className="text-sm text-gray-900">{analysisData.email_providers.join(', ')}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Security & Compliance */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <ShieldCheckIcon className="h-5 w-5 mr-2 text-green-600" />
                          Security Analysis
                        </h3>
                        <div className="space-y-4">
                          {/* Security Headers Chart */}
                          {generateSecurityHeadersData(analysisData) && (
                            <div className="h-48">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Security Headers</h4>
                              <Doughnut data={generateSecurityHeadersData(analysisData)!} options={chartOptions} />
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            <div>
                              <span className="text-sm font-medium text-gray-500">Security Grade</span>
                              <div className={`text-lg font-semibold ${getGradeColor(analysisData.security_grade || 'F').replace('border-', 'text-').replace('bg-', '').replace('-50', '-600')}`}>
                                {analysisData.security_grade || 'N/A'}
                              </div>
                            </div>
                            {analysisData.ssl_info && (
                              <>
                                <div>
                                  <span className="text-sm font-medium text-gray-500">SSL Issuer</span>
                                  <div className="text-sm text-gray-900">{analysisData.ssl_info.issuer || 'Unknown'}</div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-500">SSL Valid Until</span>
                                  <div className="text-sm text-gray-900">{analysisData.ssl_info.valid_until || 'Unknown'}</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact & Business Intelligence */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Contact Information */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-600" />
                          Contact Intelligence
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            {analysisData.contact_info && analysisData.contact_info.emails && analysisData.contact_info.emails.length > 0 && (
                              <div className="mb-4">
                                <span className="text-sm font-medium text-gray-500">Email Addresses ({analysisData.contact_info.emails.length})</span>
                                <ul className="mt-1 space-y-1">
                                  {analysisData.contact_info.emails.slice(0, 3).map((email, index) => (
                                    <li key={index} className="text-sm text-gray-700">{email}</li>
                                  ))}
                                  {analysisData.contact_info.emails.length > 3 && (
                                    <li className="text-sm text-gray-500">+{analysisData.contact_info.emails.length - 3} more</li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            {analysisData.contact_info && analysisData.contact_info.phones && analysisData.contact_info.phones.length > 0 && (
                              <div>
                                <span className="text-sm font-medium text-gray-500">Phone Numbers ({analysisData.contact_info.phones.length})</span>
                                <ul className="mt-1 space-y-1">
                                  {analysisData.contact_info.phones.slice(0, 3).map((phone, index) => (
                                    <li key={index} className="text-sm text-gray-700">{phone}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            {analysisData.contact_info && analysisData.contact_info.social_media && Object.keys(analysisData.contact_info.social_media).length > 0 && (
                              <div className="mb-4">
                                <span className="text-sm font-medium text-gray-500">Social Media ({Object.keys(analysisData.contact_info.social_media).length})</span>
                                <ul className="mt-1 space-y-1">
                                  {Object.entries(analysisData.contact_info.social_media).map(([platform, url]) => (
                                    <li key={platform} className="text-sm">
                                      <span className="text-gray-500 capitalize">{platform}:</span>
                                      <span className="ml-1 text-blue-600 truncate">{url}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {analysisData.whois_info && (
                              <div>
                                <span className="text-sm font-medium text-gray-500">Domain Registration</span>
                                <div className="mt-1 space-y-1">
                                  <div className="text-sm text-gray-700">
                                    <span className="text-gray-500">Registrar:</span> {analysisData.whois_info.registrar || 'Unknown'}
                                  </div>
                                  <div className="text-sm text-gray-700">
                                    <span className="text-gray-500">Created:</span> {analysisData.whois_info.created_date || 'Unknown'}
                                  </div>
                                  <div className="text-sm text-gray-700">
                                    <span className="text-gray-500">Expires:</span> {analysisData.whois_info.expiry_date || 'Unknown'}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Competitive Intelligence */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-green-600" />
                          Competitive Intelligence
                        </h3>
                        <div className="space-y-4">
                          {analysisData.competitive_advantages && analysisData.competitive_advantages.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Competitive Advantages ({analysisData.competitive_advantages.length})</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {analysisData.competitive_advantages.slice(0, 6).map((advantage, index) => (
                                  <span key={index} className="inline-block px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                    {advantage}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {analysisData.key_features && analysisData.key_features.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Key Features ({analysisData.key_features.length})</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {analysisData.key_features.slice(0, 6).map((feature, index) => (
                                  <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {analysisData.partnerships && analysisData.partnerships.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Partnerships ({analysisData.partnerships.length})</span>
                              <ul className="mt-1 space-y-1">
                                {analysisData.partnerships.slice(0, 4).map((partnership, index) => (
                                  <li key={index} className="text-sm text-gray-700">• {partnership}</li>
                                ))}
                                {analysisData.partnerships.length > 4 && (
                                  <li className="text-sm text-gray-500">+{analysisData.partnerships.length - 4} more partnerships</li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {analysisData.certifications && analysisData.certifications.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Certifications ({analysisData.certifications.length})</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {analysisData.certifications.map((cert, index) => (
                                  <span key={index} className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                                    {cert}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* SEO & Digital Presence Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* SEO Analysis */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">SEO Analysis</h3>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm font-medium text-gray-500">Meta Tags</span>
                            <div className="text-lg font-semibold text-gray-900">{analysisData.seo_metrics?.meta_tags_count || 0}</div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">OG Tags</span>
                            <div className="text-lg font-semibold text-gray-900">{analysisData.seo_metrics?.og_tags_count || 0}</div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">Schema.org</span>
                            {analysisData.seo_metrics?.schema_org ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">Multilingual</span>
                            {analysisData.seo_metrics?.multilingual ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Compliance Score */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Compliance</h3>
                        <div className="space-y-4">
                          {/* Compliance Chart */}
                          <div className="h-48">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Compliance Overview</h4>
                            <Doughnut data={generateComplianceData(analysisData)} options={chartOptions} />
                          </div>
                          
                          {analysisData.compliance_info?.compliance_score !== undefined && (
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-500">Compliance Score</span>
                                <span className="text-lg font-bold text-gray-900">{analysisData.compliance_info.compliance_score}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    analysisData.compliance_info.compliance_score >= 80 ? 'bg-green-600' :
                                    analysisData.compliance_info.compliance_score >= 60 ? 'bg-yellow-600' :
                                    'bg-red-600'
                                  }`}
                                  style={{ width: `${analysisData.compliance_info.compliance_score}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            {[
                              { key: 'privacy_policy', label: 'Privacy Policy' },
                              { key: 'terms_of_service', label: 'Terms of Service' },
                              { key: 'cookie_policy', label: 'Cookie Policy' },
                              { key: 'gdpr_compliant', label: 'GDPR Compliant' }
                            ].map(({ key, label }) => (
                              <div key={key} className="flex items-center justify-between">
                                <span className="text-sm text-gray-700">{label}</span>
                                {analysisData.compliance_info && analysisData.compliance_info[key as keyof typeof analysisData.compliance_info] ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Traffic & Performance */}
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Digital Presence</h3>
                        <div className="space-y-3">
                          {analysisData.traffic_indicators && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Estimated Traffic</span>
                              <div className="text-lg font-semibold text-gray-900">{analysisData.traffic_indicators.estimated_traffic || 'Unknown'}</div>
                            </div>
                          )}
                          
                          {analysisData.mobile_info && (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700">Responsive Design</span>
                                {analysisData.mobile_info.responsive_design ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700">iOS App</span>
                                {analysisData.mobile_info.ios_app_url ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700">Android App</span>
                                {analysisData.mobile_info.android_app_url ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                            </>
                          )}
                          
                          {analysisData.endpoints_found && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">Common Endpoints</span>
                              <div className="text-lg font-semibold text-gray-900">
                                {Object.values(analysisData.endpoints_found).filter(Boolean).length} / {Object.keys(analysisData.endpoints_found).length} found
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* DNS Records & Technical Details */}
                    {analysisData.dns_records && (
                      <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                          <GlobeAltIcon className="h-6 w-6 mr-2 text-blue-600" />
                          DNS Infrastructure Analysis
                        </h3>
                        
                        {/* DNS Records Chart */}
                        {generateDNSRecordsData(analysisData) && (
                          <div className="mb-8">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">DNS Records Distribution</h4>
                            <div className="h-64">
                              <Bar data={generateDNSRecordsData(analysisData)!} options={barOptions} />
                            </div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {analysisData.dns_records.a_records && analysisData.dns_records.a_records.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">A Records (IPv4) - {analysisData.dns_records.a_records.length}</h4>
                              <ul className="space-y-1">
                                {analysisData.dns_records.a_records.slice(0, 5).map((ip, index) => (
                                  <li key={index} className="text-sm font-mono text-gray-600">{ip}</li>
                                ))}
                                {analysisData.dns_records.a_records.length > 5 && (
                                  <li className="text-sm text-gray-500">+{analysisData.dns_records.a_records.length - 5} more</li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {analysisData.dns_records.mx_records && analysisData.dns_records.mx_records.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">MX Records - {analysisData.dns_records.mx_records.length}</h4>
                              <ul className="space-y-1">
                                {analysisData.dns_records.mx_records.slice(0, 3).map((mx, index) => (
                                  <li key={index} className="text-sm">
                                    <span className="font-mono text-gray-600">{mx.server}</span>
                                    <span className="ml-2 text-xs text-gray-500">(Priority: {mx.priority})</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {analysisData.dns_records.txt_records && analysisData.dns_records.txt_records.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">TXT Records - {analysisData.dns_records.txt_records.length}</h4>
                              <ul className="space-y-1">
                                {analysisData.dns_records.txt_records.slice(0, 3).map((txt, index) => (
                                  <li key={index} className="text-xs font-mono text-gray-600 truncate" title={txt}>
                                    {txt.length > 50 ? `${txt.substring(0, 50)}...` : txt}
                                  </li>
                                ))}
                                {analysisData.dns_records.txt_records.length > 3 && (
                                  <li className="text-sm text-gray-500">+{analysisData.dns_records.txt_records.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {analysisData.dns_records.ns_records && analysisData.dns_records.ns_records.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">NS Records - {analysisData.dns_records.ns_records.length}</h4>
                              <ul className="space-y-1">
                                {analysisData.dns_records.ns_records.map((ns, index) => (
                                  <li key={index} className="text-sm font-mono text-gray-600">{ns}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Analysis Metadata */}
                    <div className="bg-gray-50 shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Analysis Metadata</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Last Analyzed</span>
                          <div className="text-sm text-gray-900">
                            {analysisData.last_analyzed ? new Date(analysisData.last_analyzed).toLocaleString() : 'Unknown'}
                          </div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Analysis Count</span>
                          <div className="text-sm text-gray-900">{analysisData.analysis_count || 1} time{(analysisData.analysis_count || 1) > 1 ? 's' : ''}</div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Data Points Collected</span>
                          <div className="text-sm text-gray-900">150+ metrics analyzed</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Analyses */}
              {!analysisData && !loading && recentAnalyses.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Recent Analyses</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Domain
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Industry
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Company Size
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            BI Score
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Security
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Analyzed
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentAnalyses.map((analysis) => (
                          <tr
                            key={analysis.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setDomain(analysis.domain_name);
                              setAnalysisData(analysis);
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {analysis.domain_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {analysis.primary_industry || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {analysis.company_size || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                (analysis.overall_bi_score || 0) >= 70 ? 'bg-green-100 text-green-800' :
                                (analysis.overall_bi_score || 0) >= 40 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {analysis.overall_bi_score || 0}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                analysis.security_grade === 'A' ? 'bg-green-100 text-green-800' :
                                analysis.security_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                                analysis.security_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {analysis.security_grade || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {analysis.analysis_date ? new Date(analysis.analysis_date).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Tab.Panel>

            {/* Email Intelligence Panel */}
            <Tab.Panel>
              <div className="bg-white shadow rounded-lg p-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter domain name (e.g., example.com)"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchDmarcData()}
                        disabled={loadingEmail}
                      />
                    </div>
                  </div>
                  <button
                    onClick={fetchDmarcData}
                    disabled={loadingEmail}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingEmail ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <ChartBarIcon className="-ml-1 mr-2 h-4 w-4" />
                        Analyze
                      </>
                    )}
                  </button>
                </div>

                {loadingEmail && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-md">
                    <div className="flex">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-700">
                          Fetching DMARC data for {domain}...
                        </p>
                        <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {emailError && (
                  <div className="mt-4 p-4 bg-red-50 rounded-md">
                    <div className="flex">
                      <ShieldExclamationIcon className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">{emailError}</p>
                    </div>
                  </div>
                )}

                {!loadingEmail && !emailError && dmarcDomains.length === 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                    <div className="flex">
                      <ShieldExclamationIcon className="h-5 w-5 text-yellow-400 mr-2" />
                      <p className="text-sm text-yellow-700">No DMARC data found for {domain}. Please ensure the domain is registered and monitored.</p>
                    </div>
                  </div>
                )}

                {!loadingEmail && !emailError && dmarcDomains.length > 0 && (
                  <div className="space-y-6">
                    {/* Key Statistics Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <InboxIcon className="h-8 w-8 text-blue-500" />
                            </div>
                            <div className="ml-4">
                              <dt className="text-sm font-medium text-gray-500 truncate">Total Domains</dt>
                              <dd className="mt-1 text-2xl font-semibold text-gray-900">{emailStats?.totalDomains.toLocaleString()}</dd>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <ChartPieIcon className="h-8 w-8 text-green-500" />
                            </div>
                            <div className="ml-4">
                              <dt className="text-sm font-medium text-gray-500 truncate">Total Messages</dt>
                              <dd className="mt-1 text-2xl font-semibold text-gray-900">{emailStats?.totalMessages.toLocaleString()}</dd>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <CheckCircleIcon className="h-8 w-8 text-green-500" />
                            </div>
                            <div className="ml-4">
                              <dt className="text-sm font-medium text-gray-500 truncate">Avg Compliance</dt>
                              <dd className="mt-1 text-2xl font-semibold text-gray-900">{(emailStats?.avgComplianceRate || 0).toFixed(1)}%</dd>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Statistics Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <h3 className="text-sm font-medium text-gray-900 mb-3">Policy Distribution</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Reject Policy</span>
                              <span className="text-sm font-semibold text-red-600">{emailStats?.domainsWithRejectPolicy.toLocaleString()} domains</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Quarantine Policy</span>
                              <span className="text-sm font-semibold text-yellow-600">{emailStats?.domainsWithQuarantinePolicy.toLocaleString()} domains</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">None Policy</span>
                              <span className="text-sm font-semibold text-gray-600">{emailStats?.domainsWithNonePolicy.toLocaleString()} domains</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <h3 className="text-sm font-medium text-gray-900 mb-3">Top Insights</h3>
                          <div className="space-y-2">
                            <div className="flex items-start">
                              <InformationCircleIcon className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                              <span className="text-sm text-gray-600">
                                {emailStats && emailStats.domainsWithRejectPolicy > 0 ? 
                                  `${((emailStats.domainsWithRejectPolicy / emailStats.totalDomains) * 100).toFixed(1)}% domains use reject policy` :
                                  'Most domains not using strict policy'
                                }
                              </span>
                            </div>
                            <div className="flex items-start">
                              <InformationCircleIcon className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                              <span className="text-sm text-gray-600">
                                {dmarcDomains.length > 0 ? 
                                  `Highest volume: ${(Number(dmarcDomains[0].total_messages) || 0).toLocaleString()} msgs` :
                                  'No message data available'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Policy Distribution */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ShieldExclamationIcon className="h-6 w-6 mr-2 text-red-600" />
                        DMARC Policy Distribution
                      </h3>
                      <div className="h-64">
                        <Doughnut data={generatePolicyDistributionChart()} options={chartOptions} />
                      </div>
                    </div>

                    {/* Compliance Overview */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <CheckCircleIcon className="h-6 w-6 mr-2 text-green-600" />
                        Compliance Overview
                      </h3>
                      <div className="h-64">
                        <Doughnut data={generateComplianceOverviewChart()} options={chartOptions} />
                      </div>
                    </div>

                    {/* Failure Types */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-yellow-600" />
                        Failure Types
                      </h3>
                      <div className="h-64">
                        <Bar data={generateFailureTypesChart()} options={barOptions} />
                      </div>
                    </div>

                    {/* Top Domains by Messages */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ServerIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Top Domains by Messages
                      </h3>
                      <div className="h-64">
                        <Bar data={generateTopDomainsChart()} options={barOptions} />
                      </div>
                    </div>

                    {/* DMARC Domains Table */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <EnvelopeOpenIcon className="h-6 w-6 mr-2 text-purple-600" />
                        DMARC Domains ({totalDomains.toLocaleString()} total, showing {((currentPage - 1) * domainsPerPage + 1).toLocaleString()}-{Math.min(currentPage * domainsPerPage, totalDomains).toLocaleString()})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 sticky left-0 bg-white z-10"
                                onClick={() => handleSort('domain_name')}
                              >
                                Domain
                                {sortBy === 'domain_name' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('dmarc_policy')}
                              >
                                DMARC Policy
                                {sortBy === 'dmarc_policy' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('subdomain_policy')}
                              >
                                Subdomain Policy
                                {sortBy === 'subdomain_policy' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('percentage')}
                              >
                                dmarc_domains.compliant_pct
                                {sortBy === 'percentage' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('mail_sources')}
                              >
                                Mail Sources
                                {sortBy === 'mail_sources' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('total_messages')}
                              >
                                Total Messages
                                {sortBy === 'total_messages' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('compliant_messages')}
                              >
                                Compliant
                                {sortBy === 'compliant_messages' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('compliant_pct')}
                              >
                                Compliance %
                                {sortBy === 'compliant_pct' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('spf_fail_count')}
                              >
                                SPF Fails
                                {sortBy === 'spf_fail_count' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('dkim_fail_count')}
                              >
                                DKIM Fails
                                {sortBy === 'dkim_fail_count' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('non_compliant')}
                              >
                                Non-Compliant
                                {sortBy === 'non_compliant' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('forward_count')}
                              >
                                Forwarded
                                {sortBy === 'forward_count' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('unknown_count')}
                              >
                                Unknown
                                {sortBy === 'unknown_count' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort('total_wf')}
                              >
                                Total WF
                                {sortBy === 'total_wf' && (
                                  <span className="ml-1">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Report
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {dmarcDomains.map((domain) => (
                              <tr key={domain.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                                  {domain.domain_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    domain.dmarc_policy === 'reject' ? 'bg-red-100 text-red-800' :
                                    domain.dmarc_policy === 'quarantine' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {domain.dmarc_policy}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    domain.subdomain_policy === 'reject' ? 'bg-red-100 text-red-800' :
                                    domain.subdomain_policy === 'quarantine' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {domain.subdomain_policy}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {Number(domain.percentage) || 100}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.mail_sources) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.total_messages) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.compliant_messages) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    Number(domain.compliant_pct) >= 90 ? 'bg-green-100 text-green-800' :
                                    Number(domain.compliant_pct) >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {(Number(domain.compliant_pct) || 0).toFixed(1)}%
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={Number(domain.spf_fail_count) > 0 ? 'text-red-600 font-semibold' : ''}>
                                    {(Number(domain.spf_fail_count) || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={Number(domain.dkim_fail_count) > 0 ? 'text-red-600 font-semibold' : ''}>
                                    {(Number(domain.dkim_fail_count) || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={Number(domain.non_compliant) > 0 ? 'text-red-600 font-semibold' : ''}>
                                    {(Number(domain.non_compliant) || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.forward_count) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.unknown_count) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(Number(domain.total_wf) || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {domain.last_report_received ? new Date(domain.last_report_received).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    domain.status === 'active' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {domain.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination Controls */}
                      {totalDomains > domainsPerPage && (
                        <div className="mt-6 flex items-center justify-between">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage * domainsPerPage >= totalDomains}
                              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Showing{' '}
                                <span className="font-medium">{((currentPage - 1) * domainsPerPage + 1).toLocaleString()}</span>
                                {' '}to{' '}
                                <span className="font-medium">{Math.min(currentPage * domainsPerPage, totalDomains).toLocaleString()}</span>
                                {' '}of{' '}
                                <span className="font-medium">{totalDomains.toLocaleString()}</span>
                                {' '}results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Previous</span>
                                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                                {/* Page numbers */}
                                {renderPageNumbers()}
                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage * domainsPerPage >= totalDomains}
                                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Next</span>
                                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Future Email Intelligence Capabilities */}
                <div className="mt-12 space-y-8">
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg p-8">
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                      <SparklesIcon className="h-8 w-8 mr-3" />
                      Email Intelligence - Roadmapped Capabilities
                    </h2>
                    <p className="text-purple-100 text-lg">
                      Comprehensive email tracking and analysis platform with real-time insights
                    </p>
                  </div>

                  {/* Google Business Account Section */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <svg className="h-6 w-6 mr-2" viewBox="0 0 48 48">
                        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                      </svg>
                      Google Business Account Integration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Account Information</h4>
                        <dl className="space-y-2">
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-sm text-gray-600">Account Name</dt>
                            <dd className="text-sm font-medium text-gray-900">Pager.ai Technologies Inc.</dd>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-sm text-gray-600">Primary Email</dt>
                            <dd className="text-sm font-medium text-gray-900">business@Pager.ai.com</dd>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-sm text-gray-600">Workspace Users</dt>
                            <dd className="text-sm font-medium text-gray-900">24 active users</dd>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-sm text-gray-600">Daily Email Volume</dt>
                            <dd className="text-sm font-medium text-gray-900">~3,500 emails/day</dd>
                          </div>
                        </dl>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Email Tracking Capabilities</h4>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-600">Real-time SPF, DKIM, and DMARC validation</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-600">Complete header analysis and IP tracking</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-600">Content analysis and sentiment detection</span>
                          </div>
                          <div className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-600">Reply rate and engagement tracking</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email Tracking Details */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <ChartBarIcon className="h-6 w-6 mr-2 text-blue-600" />
                      Comprehensive Email Tracking
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking Element</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Sender Email</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Full sender email address with domain verification</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">sales@Pager.ai.com</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Recipient Email</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Target email with engagement tracking</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">cto@techcorp.com</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">SPF Status</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Sender Policy Framework validation</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">PASS</span>
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">DKIM Status</td>
                            <td className="px-4 py-3 text-sm text-gray-500">DomainKeys Identified Mail signature</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">PASS</span>
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">DMARC Alignment</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Domain-based Message Authentication</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">ALIGNED</span>
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Subject Line</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Email subject with sentiment analysis</td>
                            <td className="px-4 py-3 text-sm text-gray-900">Quick question about your infrastructure needs</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Content Type</td>
                            <td className="px-4 py-3 text-sm text-gray-500">MIME type and encoding</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">text/html; charset=UTF-8</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Sending IP</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Origin IP address with geolocation</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">209.85.220.41 (Google)</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Headers Analyzed</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Total email headers processed</td>
                            <td className="px-4 py-3 text-sm text-gray-900">47 headers</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Threat Score</td>
                            <td className="px-4 py-3 text-sm text-gray-500">Security threat assessment</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Low (2/100)</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Sample Cold Email Conversations */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                      <EnvelopeIcon className="h-6 w-6 mr-2 text-purple-600" />
                      Cold Email Conversations & Analytics
                    </h3>
                    
                    {/* Email Thread 1 */}
                    <div className="space-y-4 mb-8">
                      <div className="border-l-4 border-blue-500 pl-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Initial Outreach</h4>
                            <p className="text-xs text-gray-500">From: sarah@Pager.ai.com → To: david@startup.io</p>
                            <p className="text-xs text-gray-500">Sent: Nov 15, 2024 9:23 AM</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">SPF ✓</span>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">DKIM ✓</span>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">DMARC ✓</span>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3 text-sm text-gray-700">
                          <p className="font-medium mb-2">Subject: Your recent ProductHunt launch - impressive traction!</p>
                          <p className="mb-2">Hi David,</p>
                          <p className="mb-2">I noticed startup.io hit #3 on ProductHunt last week - congrats! The AI-powered analytics feature looks particularly interesting.</p>
                          <p className="mb-2">I'm reaching out because we help fast-growing SaaS companies like yours ensure email deliverability as they scale their outreach. Given your recent growth, I imagine email infrastructure might be on your radar?</p>
                          <p className="mb-2">Would you be open to a quick 15-min call next week to discuss how we could help?</p>
                          <p>Best,<br/>Sarah</p>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center"><EyeIcon className="h-3 w-3 mr-1" />Opened: 3 times</span>
                          <span className="flex items-center"><LinkIcon className="h-3 w-3 mr-1" />Links clicked: 1</span>
                          <span className="flex items-center"><ClockIcon className="h-3 w-3 mr-1" />Read time: 45s</span>
                        </div>
                      </div>

                      <div className="border-l-4 border-green-500 pl-4 ml-8">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Reply Received</h4>
                            <p className="text-xs text-gray-500">From: david@startup.io → To: sarah@Pager.ai.com</p>
                            <p className="text-xs text-gray-500">Sent: Nov 15, 2024 2:47 PM</p>
                          </div>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Positive Sentiment</span>
                        </div>
                        <div className="bg-green-50 rounded p-3 text-sm text-gray-700">
                          <p className="font-medium mb-2">Re: Your recent ProductHunt launch - impressive traction!</p>
                          <p className="mb-2">Hi Sarah,</p>
                          <p className="mb-2">Thanks for reaching out and the kind words! You're right - we're actually dealing with some deliverability issues as we scale our user onboarding emails.</p>
                          <p className="mb-2">I'd be interested in learning more. How about Tuesday at 2 PM PST?</p>
                          <p>David</p>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center"><ClockIcon className="h-3 w-3 mr-1" />Response time: 5h 24m</span>
                          <span className="flex items-center text-green-600"><CheckCircleIcon className="h-3 w-3 mr-1" />Meeting booked</span>
                        </div>
                      </div>
                    </div>

                    {/* Email Thread 2 */}
                    <div className="space-y-4">
                      <div className="border-l-4 border-blue-500 pl-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Follow-up Sequence</h4>
                            <p className="text-xs text-gray-500">From: mike@Pager.ai.com → To: lisa@enterprise.com</p>
                            <p className="text-xs text-gray-500">Sent: Nov 14, 2024 10:15 AM</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">SPF ✓</span>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">DKIM ✓</span>
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">DMARC ?</span>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3 text-sm text-gray-700">
                          <p className="font-medium mb-2">Subject: Quick follow-up - Email security for enterprise.com</p>
                          <p className="mb-2">Hi Lisa,</p>
                          <p className="mb-2">I wanted to follow up on my previous email about improving enterprise.com's email authentication.</p>
                          <p className="mb-2">I noticed your domain currently has a "none" DMARC policy, which could be exposing you to spoofing attacks. We recently helped TechGiant Corp move to a "reject" policy without any disruption to legitimate emails.</p>
                          <p className="mb-2">Would it be helpful if I shared a quick security assessment of your current setup?</p>
                          <p>Mike</p>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center"><EyeIcon className="h-3 w-3 mr-1" />Opened: 1 time</span>
                          <span className="flex items-center"><XCircleIcon className="h-3 w-3 mr-1 text-red-500" />No reply</span>
                          <span className="flex items-center"><ArrowPathIcon className="h-3 w-3 mr-1" />Sequence: Email 2 of 4</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email Analytics Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Email Volume Chart */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ChartPieIcon className="h-6 w-6 mr-2 text-indigo-600" />
                        Email Volume by Category
                      </h3>
                      <div className="h-64">
                        <Doughnut 
                          data={{
                            labels: ['Cold Outreach', 'Follow-ups', 'Replies', 'Newsletters', 'Transactional'],
                            datasets: [{
                              data: [2847, 1523, 892, 634, 421],
                              backgroundColor: [
                                'rgba(99, 102, 241, 0.8)',
                                'rgba(139, 92, 246, 0.8)',
                                'rgba(34, 197, 94, 0.8)',
                                'rgba(251, 146, 60, 0.8)',
                                'rgba(96, 165, 250, 0.8)'
                              ],
                              borderWidth: 0
                            }]
                          }}
                          options={chartOptions}
                        />
                      </div>
                    </div>

                    {/* Engagement Metrics */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ChartBarIcon className="h-6 w-6 mr-2 text-green-600" />
                        Engagement Metrics
                      </h3>
                      <div className="h-64">
                        <Bar 
                          data={{
                            labels: ['Open Rate', 'Click Rate', 'Reply Rate', 'Bounce Rate', 'Unsubscribe'],
                            datasets: [{
                              label: 'Industry Average',
                              data: [21.5, 2.3, 8.5, 2.1, 0.5],
                              backgroundColor: 'rgba(156, 163, 175, 0.5)',
                              borderColor: 'rgba(156, 163, 175, 1)',
                              borderWidth: 1
                            }, {
                              label: 'Your Performance',
                              data: [34.2, 5.7, 14.3, 0.8, 0.2],
                              backgroundColor: 'rgba(34, 197, 94, 0.5)',
                              borderColor: 'rgba(34, 197, 94, 1)',
                              borderWidth: 1
                            }]
                          }}
                          options={{
                            ...barOptions,
                            scales: {
                              ...barOptions.scales,
                              y: {
                                ...barOptions.scales?.y,
                                ticks: {
                                  callback: function(value: any) {
                                    return value + '%';
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Response Time Distribution */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <ClockIcon className="h-6 w-6 mr-2 text-purple-600" />
                        Response Time Distribution
                      </h3>
                      <div className="h-64">
                        <Line 
                          data={{
                            labels: ['< 1h', '1-3h', '3-6h', '6-12h', '12-24h', '1-3d', '3-7d', '> 7d'],
                            datasets: [{
                              label: 'Responses',
                              data: [142, 287, 198, 156, 234, 178, 89, 34],
                              borderColor: 'rgba(139, 92, 246, 1)',
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              tension: 0.4
                            }]
                          }}
                          options={chartOptions}
                        />
                      </div>
                    </div>

                    {/* Sentiment Analysis */}
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FaceSmileIcon className="h-6 w-6 mr-2 text-yellow-600" />
                        Reply Sentiment Analysis
                      </h3>
                      <div className="h-64">
                        <Doughnut 
                          data={{
                            labels: ['Positive', 'Neutral', 'Negative', 'Very Positive', 'Very Negative'],
                            datasets: [{
                              data: [412, 287, 98, 156, 23],
                              backgroundColor: [
                                'rgba(34, 197, 94, 0.8)',
                                'rgba(156, 163, 175, 0.8)',
                                'rgba(251, 146, 60, 0.8)',
                                'rgba(16, 185, 129, 0.8)',
                                'rgba(239, 68, 68, 0.8)'
                              ],
                              borderWidth: 0
                            }]
                          }}
                          options={chartOptions}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Advanced Analytics */}
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <LightBulbIcon className="h-6 w-6 mr-2 text-yellow-500" />
                      AI-Powered Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">Best Performing Subject Lines</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Questions get 43% higher open rates</li>
                          <li>• Personalization increases replies by 31%</li>
                          <li>• Shorter subjects (5-7 words) perform best</li>
                        </ul>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-2">Optimal Send Times</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>• Tuesday 10-11 AM: 38% open rate</li>
                          <li>• Thursday 2-3 PM: 35% open rate</li>
                          <li>• Avoid Mondays and Fridays</li>
                        </ul>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-900 mb-2">Content Recommendations</h4>
                        <ul className="text-sm text-purple-700 space-y-1">
                          <li>• 50-125 words ideal for cold emails</li>
                          <li>• 1-2 links maximum per email</li>
                          <li>• Clear CTA increases replies by 28%</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default BusinessIntelligence; 